/* eslint-disable no-nested-ternary */
/* eslint-disable react/forbid-prop-types */
import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Typography, alpha, TextField, MenuItem, IconButton,
    Stack, Chip, FormControlLabel, Switch, Tooltip, Skeleton,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import { common } from 'src/theme/palette';
import useAuth from 'src/hooks/useAuth';
import { useSelector } from 'src/redux/store';
// templatePreviews are no longer used here — the right-rail
// SendFormsPreview is the single canonical preview now. Keeping the
// shared module untouched (still imported by Practice Settings).
import { normaliseEmails, isValidEmail } from './sendFormsUtils';

/* ──────────────────────────────────────────────────────────────────────────
   ChannelComposer
   ─────────────
   Right-column compose pane. Renders:
     1. Four channel cards (Email · WhatsApp · Copy link · QR code)
     2. Channel-specific options:
          EMAIL    → template picker, BCC controls
          WHATSAPP → template picker
          COPY     → static info
          QR       → static info ("we'll show the QR after send")
     3. A live preview pane that reflects the chosen channel.
   ────────────────────────────────────────────────────────────────────────── */

const CHANNEL_OPTIONS = [
    {
        key: 'EMAIL',
        label: 'Email',
        icon: 'solar:letter-bold-duotone',
        description: 'Send via email',
        requiresContact: 'email',
    },
    {
        key: 'WHATSAPP',
        label: 'WhatsApp',
        icon: 'mdi:whatsapp',
        description: 'Send via WhatsApp',
        requiresContact: 'phone',
    },
    {
        key: 'COPY',
        label: 'Copy link',
        icon: 'solar:link-round-bold-duotone',
        description: 'Copy & share manually',
    },
    {
        key: 'QR',
        label: 'QR code',
        icon: 'solar:qr-code-bold-duotone',
        description: 'Print or display in clinic',
    },
];

const ChannelComposer = ({
    isAnonymous,
    audienceMode,
    channels,
    setChannels,
    patientEmail,
    patientPhone,
    emailTemplateId,
    setEmailTemplateId,
    whatsappTemplateId,
    setWhatsappTemplateId,
    emailTemplates,
    whatsappTemplates,
    // emailTemplate / whatsappTemplate were used by the in-line preview
    // block that this component used to host. The block was removed
    // 2026-04-27; the right-rail SendFormsPreview is now canonical and
    // receives these props directly from the parent. Kept the prop
    // names off the signature to avoid no-unused-vars; the parent
    // simply stops passing them here.
    templatesLoading,
    bccEnabled,
    setBccEnabled,
    bccList,
    setBccList,
    inlineTemplateSlot,
}) => {
    // Helpers — keep code below readable. ``channels`` is the source of
    // truth; we derive everything else from it.
    const isChannelSelected = (key) => channels.includes(key);
    const primaryChannel = channels[0] || 'EMAIL';

    // Toggle a channel in/out. Enforces "at least one channel must remain
    // selected" — if the user tries to deselect their last channel, we
    // refuse and let the disabled-state styling explain why. Order is
    // preserved so the FIRST selected channel stays the "primary" (drives
    // the bottom-of-card preview pane and the snackbar text).
    const toggleChannel = (key) => {
        if (channels.includes(key)) {
            if (channels.length <= 1) return; // can't unselect the last one
            setChannels(channels.filter((c) => c !== key));
        } else {
            setChannels([...channels, key]);
        }
    };
    const { user } = useAuth();
    const practiceUserState = useSelector((state) => state.practiceUser?.practiceUser);

    // ── BCC modes: 'me' | 'team' | 'custom' ────────────────────────────────
    const [bccMode, setBccMode] = useState('me');
    const [customEmailDraft, setCustomEmailDraft] = useState('');

    // Resolve "BCC me" address from auth user
    const meEmail = (user?.email || '').trim().toLowerCase();

    // Resolve "BCC team" — practice users with email
    const teamEmails = useMemo(() => {
        const results = practiceUserState?.results || [];
        return results
            .map((pu) => (pu?.user?.email || '').trim().toLowerCase())
            .filter((e) => e && e !== meEmail);
    }, [practiceUserState, meEmail]);

    // ── Sync BCC list when toggle/mode changes ─────────────────────────────
    useEffect(() => {
        if (!bccEnabled) {
            setBccList([]);
            return;
        }
        if (bccMode === 'me' && meEmail) {
            setBccList(normaliseEmails([meEmail]));
        } else if (bccMode === 'team') {
            setBccList(normaliseEmails([meEmail, ...teamEmails]));
        }
        // 'custom' is managed by the chip input
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bccEnabled, bccMode, meEmail, teamEmails.join(',')]);

    const addCustomEmail = () => {
        const next = customEmailDraft.trim().toLowerCase();
        if (!next || !isValidEmail(next)) return;
        // Server enforces a 5-recipient BCC ceiling for consent emails — this
        // mirrors that limit so the user gets immediate feedback instead of a
        // 400 from the API.
        if (bccList.length >= 5) return;
        const merged = normaliseEmails([...bccList, next]);
        setBccList(merged.slice(0, 5));
        setCustomEmailDraft('');
    };

    const removeBccEntry = (addr) => {
        setBccList(bccList.filter((e) => e !== addr));
    };

    // ── Channel disable matrix ────────────────────────────────────────────
    //
    // Mode → allowed channels (from send-forms API contract):
    //   ONE_PATIENT  → EMAIL, WHATSAPP, COPY, QR (all four)
    //   PATIENT_LIST → EMAIL, WHATSAPP only (no Copy / QR — broadcast must
    //                  hit a recipient over an addressable channel)
    //   ANONYMOUS    → COPY, QR only (no recipient → nothing to email)
    //
    // ONE_PATIENT additionally requires the relevant contact field (email /
    // phone) to be populated on the patient.
    const resolvedMode = audienceMode || (isAnonymous ? 'ANONYMOUS' : 'ONE_PATIENT');

    const channelDisabledReason = (key) => {
        if (resolvedMode === 'PATIENT_LIST' && (key === 'COPY' || key === 'QR')) {
            return 'Use One patient or Anonymous mode for direct sharing';
        }
        if (resolvedMode === 'ANONYMOUS' && (key === 'EMAIL' || key === 'WHATSAPP')) {
            return 'Use Patient list mode to send broadcasts via Email/WhatsApp';
        }
        if (resolvedMode === 'ONE_PATIENT') {
            if (key === 'EMAIL' && !patientEmail) return 'Patient has no email on file';
            if (key === 'WHATSAPP' && !patientPhone) return 'Patient has no phone on file';
        }
        return '';
    };

    const isChannelDisabled = (key) => Boolean(channelDisabledReason(key));

    // When the audience mode changes, drop any channels that are no longer
    // allowed. Always keep at least one valid channel selected (fallback to
    // the first valid candidate) so the wizard never lands in a no-channel
    // state.
    useEffect(() => {
        const allowed = channels.filter((c) => !isChannelDisabled(c));
        if (allowed.length === channels.length) return; // no change needed
        if (allowed.length > 0) {
            setChannels(allowed);
            return;
        }
        const candidates = ['EMAIL', 'WHATSAPP', 'COPY', 'QR'];
        const fallback = candidates.find((c) => !isChannelDisabled(c));
        if (fallback) setChannels([fallback]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolvedMode, patientEmail, patientPhone]);

    return (
        <Stack spacing={2}>
            <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography
                        variant="subtitle2"
                        sx={{
                            fontWeight: 700, fontSize: '10.5px', color: 'text.secondary',
                            textTransform: 'uppercase', letterSpacing: '0.5px',
                        }}
                    >
                        Channels
                    </Typography>
                    {/* Multi-channel hint — explains the new behaviour with
                        zero handholding so power users get on with it. */}
                    <Typography
                        variant="caption"
                        sx={{
                            fontSize: '10.5px', color: '#637381', fontStyle: 'italic',
                        }}
                    >
                        Select one or more
                    </Typography>
                </Stack>
                <Box
                    role="group"
                    aria-label="Delivery channels"
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: 1,
                    }}
                >
                    {CHANNEL_OPTIONS.map((opt) => {
                        const disabled = isChannelDisabled(opt.key);
                        const selected = isChannelSelected(opt.key);
                        // The very last selected channel can't be turned
                        // off — explain that with the cursor + tooltip
                        // rather than just silently no-op'ing.
                        const isLastSelected = selected && channels.length === 1;
                        const tooltipMsg = disabled
                            ? channelDisabledReason(opt.key)
                            : (isLastSelected ? 'At least one channel must be selected' : '');
                        return (
                            <Tooltip
                                key={opt.key}
                                title={tooltipMsg}
                                disableInteractive
                            >
                                <Box
                                    role="checkbox"
                                    aria-checked={selected}
                                    aria-disabled={disabled}
                                    tabIndex={disabled ? -1 : 0}
                                    onClick={() => !disabled && toggleChannel(opt.key)}
                                    onKeyDown={(e) => {
                                        if (disabled) return;
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            toggleChannel(opt.key);
                                        }
                                    }}
                                    sx={{
                                        position: 'relative',
                                        display: 'flex', alignItems: 'center', gap: 1,
                                        px: 1.25, py: 1.25,
                                        borderRadius: '12px',
                                        border: '1.5px solid',
                                        borderColor: selected ? common.mintyZest : 'rgba(145,158,171,0.16)',
                                        bgcolor: selected ? alpha(common.mintyZest, 0.04) : '#fff',
                                        cursor: disabled ? 'not-allowed' : 'pointer',
                                        opacity: disabled ? 0.45 : 1,
                                        outline: 'none',
                                        transition: 'all 0.15s ease',
                                        '&:hover': !disabled && {
                                            borderColor: selected ? common.mintyZest : alpha(common.mintyZest, 0.4),
                                        },
                                        '&:focus-visible': {
                                            boxShadow: `0 0 0 3px ${alpha(common.mintyZest, 0.18)}`,
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 32, height: 32, borderRadius: '8px',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            bgcolor: selected ? alpha(common.mintyZest, 0.12) : 'rgba(145,158,171,0.08)',
                                            color: selected ? common.mintyZest : '#637381',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <Iconify icon={opt.icon} width={16} />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontSize: '12.5px', fontWeight: 700,
                                                color: selected ? common.mintyZest : '#212B36',
                                                lineHeight: 1.2,
                                            }}
                                            noWrap
                                        >
                                            {opt.label}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{ fontSize: '10.5px', color: 'text.secondary', lineHeight: 1.2 }}
                                            noWrap
                                        >
                                            {opt.description}
                                        </Typography>
                                    </Box>
                                    {selected && (
                                        <Iconify
                                            icon="eva:checkmark-circle-2-fill"
                                            width={16}
                                            sx={{ color: common.mintyZest, position: 'absolute', top: 6, right: 6 }}
                                        />
                                    )}
                                </Box>
                            </Tooltip>
                        );
                    })}
                </Box>
            </Box>

            {/* ── Inline template card slot ────────────────────────────────
                When a TemplateCard stack is supplied AND at least one of
                EMAIL / WHATSAPP is selected, render it in place of the
                legacy local template pickers below. The parent wizard owns
                rendering one TemplateCard per selected outbound channel. */}
            {inlineTemplateSlot && (isChannelSelected('EMAIL') || isChannelSelected('WHATSAPP')) && (
                <Box>{inlineTemplateSlot}</Box>
            )}

            {/* ── Channel-specific options (legacy, no inline slot) ──────── */}
            {!inlineTemplateSlot && isChannelSelected('EMAIL') && (
                <Box>
                    <Typography
                        variant="subtitle2"
                        sx={{
                            fontWeight: 700, fontSize: '10.5px', color: 'text.secondary',
                            textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1,
                        }}
                    >
                        Email template
                    </Typography>
                    {templatesLoading ? (
                        <Skeleton variant="rounded" height={40} />
                    ) : (
                        <TextField
                            select
                            size="small"
                            fullWidth
                            value={emailTemplateId || ''}
                            onChange={(e) => setEmailTemplateId(e.target.value || null)}
                            placeholder="Choose a template"
                            sx={{
                                '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '13px', bgcolor: '#fff' },
                                '& .MuiSelect-select': { py: 0.85 },
                            }}
                            inputProps={{ 'aria-label': 'Email template' }}
                        >
                            <MenuItem value="" sx={{ fontSize: '13px', color: 'text.secondary' }}>
                                <em>No template (plain link)</em>
                            </MenuItem>
                            {(emailTemplates || []).map((t) => (
                                <MenuItem key={t.id} value={t.id} sx={{ fontSize: '13px' }}>
                                    {t.subject || t.name || `Template #${t.id}`}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}

                    {/* BCC */}
                    <Box sx={{ mt: 1.5 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={bccEnabled}
                                    onChange={(e) => setBccEnabled(e.target.checked)}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': { color: common.mintyZest },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: common.mintyZest },
                                    }}
                                />
                            }
                            label={
                                <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 600 }}>
                                    BCC a copy
                                </Typography>
                            }
                            sx={{ ml: 0 }}
                        />

                        {bccEnabled && (
                            <Box sx={{ mt: 1 }}>
                                <Stack direction="row" spacing={0.75} sx={{ mb: 1 }}>
                                    {[
                                        { key: 'me', label: 'BCC me' },
                                        { key: 'team', label: 'BCC team' },
                                        { key: 'custom', label: 'Custom' },
                                    ].map((m) => (
                                        <Chip
                                            key={m.key}
                                            label={m.label}
                                            size="small"
                                            clickable
                                            onClick={() => setBccMode(m.key)}
                                            sx={{
                                                height: 26, fontSize: '11.5px', fontWeight: 600,
                                                borderRadius: '7px',
                                                bgcolor: bccMode === m.key ? alpha(common.mintyZest, 0.12) : alpha('#919EAB', 0.08),
                                                color: bccMode === m.key ? common.mintyZest : '#637381',
                                                border: '1px solid',
                                                borderColor: bccMode === m.key ? alpha(common.mintyZest, 0.3) : 'transparent',
                                                '&:hover': {
                                                    bgcolor: bccMode === m.key ? alpha(common.mintyZest, 0.16) : alpha('#919EAB', 0.12),
                                                },
                                            }}
                                        />
                                    ))}
                                </Stack>

                                {bccMode === 'custom' && (
                                    <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="email@example.com"
                                            value={customEmailDraft}
                                            onChange={(e) => setCustomEmailDraft(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    addCustomEmail();
                                                }
                                            }}
                                            error={Boolean(customEmailDraft) && !isValidEmail(customEmailDraft.trim())}
                                            helperText={
                                                customEmailDraft && !isValidEmail(customEmailDraft.trim())
                                                    ? 'Enter a valid email'
                                                    : ' '
                                            }
                                            sx={{
                                                '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '13px', bgcolor: '#fff' },
                                                '& .MuiFormHelperText-root': { fontSize: '10.5px', mx: 0 },
                                            }}
                                            inputProps={{ 'aria-label': 'Add BCC email', type: 'email' }}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={addCustomEmail}
                                            disabled={!customEmailDraft || !isValidEmail(customEmailDraft.trim())}
                                            aria-label="Add BCC email"
                                            sx={{
                                                width: 36, height: 36, borderRadius: '8px',
                                                bgcolor: alpha(common.mintyZest, 0.1),
                                                color: common.mintyZest,
                                                '&:hover': { bgcolor: alpha(common.mintyZest, 0.18) },
                                                '&.Mui-disabled': { bgcolor: 'rgba(145,158,171,0.08)', color: '#919EAB' },
                                            }}
                                        >
                                            <Iconify icon="eva:plus-fill" width={16} />
                                        </IconButton>
                                    </Stack>
                                )}

                                {bccList.length > 0 && (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {bccList.map((e) => (
                                            <Chip
                                                key={e}
                                                label={e}
                                                size="small"
                                                onDelete={bccMode === 'custom' ? () => removeBccEntry(e) : undefined}
                                                deleteIcon={<Iconify icon="eva:close-fill" width={12} />}
                                                sx={{
                                                    height: 22, fontSize: '11px',
                                                    bgcolor: alpha('#078DEE', 0.08),
                                                    color: '#0451A5',
                                                    borderRadius: '6px',
                                                    '& .MuiChip-deleteIcon': { color: '#0451A5', mr: 0.25 },
                                                }}
                                            />
                                        ))}
                                    </Box>
                                )}

                                {bccList.length >= 5 && (
                                    <Typography variant="caption" sx={{ fontSize: '10.5px', color: '#B76E00', mt: 0.5, display: 'block' }}>
                                        Maximum of 5 BCC recipients reached.
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Box>
                </Box>
            )}

            {!inlineTemplateSlot && isChannelSelected('WHATSAPP') && (
                <Box>
                    <Typography
                        variant="subtitle2"
                        sx={{
                            fontWeight: 700, fontSize: '10.5px', color: 'text.secondary',
                            textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1,
                        }}
                    >
                        WhatsApp template
                    </Typography>
                    {templatesLoading ? (
                        <Skeleton variant="rounded" height={40} />
                    ) : (
                        <TextField
                            select
                            size="small"
                            fullWidth
                            value={whatsappTemplateId || ''}
                            onChange={(e) => setWhatsappTemplateId(e.target.value || null)}
                            sx={{
                                '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '13px', bgcolor: '#fff' },
                                '& .MuiSelect-select': { py: 0.85 },
                            }}
                            inputProps={{ 'aria-label': 'WhatsApp template' }}
                        >
                            <MenuItem value="" sx={{ fontSize: '13px', color: 'text.secondary' }}>
                                <em>No template (plain link)</em>
                            </MenuItem>
                            {(whatsappTemplates || []).map((t) => (
                                <MenuItem key={t.id} value={t.id} sx={{ fontSize: '13px' }}>
                                    {t.name || t.subject || `Template #${t.id}`}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                </Box>
            )}

            {/* The duplicate inline preview that used to live here was
                removed 2026-04-27. Two preview pipelines (this one and
                the right-rail SendFormsPreview) inevitably drifted —
                the inline one rendered the template's HTML correctly
                but never resolved merge tags, while the rail resolved
                tags but mangled HTML. The right rail is now canonical:
                it computes the real branded-subdomain URL, renders
                HTML email bodies properly, and stacks one mockup per
                selected channel. Keep the data-primary marker so
                anything reading it still works. */}
            <Box sx={{ display: 'none' }} aria-hidden data-primary={primaryChannel} />
        </Stack>
    );
};

ChannelComposer.propTypes = {
    isAnonymous: PropTypes.bool,
    audienceMode: PropTypes.oneOf(['ONE_PATIENT', 'PATIENT_LIST', 'ANONYMOUS']),
    channels: PropTypes.arrayOf(PropTypes.string).isRequired,
    setChannels: PropTypes.func.isRequired,
    patientEmail: PropTypes.string,
    patientPhone: PropTypes.string,
    emailTemplateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    setEmailTemplateId: PropTypes.func.isRequired,
    whatsappTemplateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    setWhatsappTemplateId: PropTypes.func.isRequired,
    emailTemplates: PropTypes.array,
    whatsappTemplates: PropTypes.array,
    templatesLoading: PropTypes.bool,
    bccEnabled: PropTypes.bool,
    setBccEnabled: PropTypes.func.isRequired,
    bccList: PropTypes.array,
    setBccList: PropTypes.func.isRequired,
    inlineTemplateSlot: PropTypes.node,
};

ChannelComposer.defaultProps = {
    isAnonymous: false,
    audienceMode: undefined,
    patientEmail: '',
    patientPhone: '',
    emailTemplateId: null,
    whatsappTemplateId: null,
    emailTemplates: [],
    whatsappTemplates: [],
    templatesLoading: false,
    bccEnabled: false,
    bccList: [],
    inlineTemplateSlot: null,
};

// EmailPreview, WhatsAppPreview, and PlainPreview are now imported from
// `src/components/templatePreviews` so the same UI is reused by the
// Practice Settings → Consent forms templates section.

export default ChannelComposer;
